import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import FormField from './FormField';
import LoadingButton from './LoadingButton';
import AccountInactiveModal from './AccountInactiveModal';
import useEmailFieldValidation from '../../hooks/useEmailFieldValidation';
import { getEmailValidationError, getEmailValidationInputClass } from '../../utils/emailValidation';
import EmailFieldFeedback from '../EmailInput/EmailFieldFeedback';

const UniversalAuthPage = ({
  type = 'login', // 'login', 'register', 'forgot-password', 'reset-password', 'two-factor'
  appType = 'main', // 'main', 'admin', 'airliner'
  title,
  subtitle,
  onSubmit,
  additionalFields = [],
  showPasswordGenerator = false,
  customValidation,
  redirectPaths = {}
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const {
    emailTouched,
    emailError,
    onEmailChange,
    onEmailBlur,
    validateEmailForSubmit,
  } = useEmailFieldValidation({
    requiredMessage:
      type === 'login' ? 'Please enter a User ID' : 'Please enter an email address',
  });

  // Theme configuration based on app type
  const getTheme = () => {
    switch (appType) {
      case 'admin':
        return {
          iconBgColor: 'bg-jet-red-100',
          iconColor: 'text-jet-red-600',
          linkColor: 'text-jet-red-600 hover:text-jet-red-700',
          buttonColor: 'bg-jet-red-600 hover:bg-jet-red-700'
        };
      case 'airliner':
        return {
          iconBgColor: 'bg-jet-yellow-100',
          iconColor: 'text-jet-yellow-600',
          linkColor: 'text-jet-yellow-600 hover:text-jet-yellow-700',
          buttonColor: 'bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900'
        };
      default:
        return {
          iconBgColor: 'bg-jet-blue-100',
          iconColor: 'text-jet-blue-600',
          linkColor: 'text-jet-blue-600 hover:text-jet-blue-700',
          buttonColor: 'bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900'
        };
    }
  };

  const theme = getTheme();

  // Icon configuration based on type
  const getIcon = () => {
    switch (type) {
      case 'login':
        return appType === 'admin' 
          ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
          : appType === 'airliner'
          ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
          : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>;
      case 'register':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>;
      case 'forgot-password':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>;
      case 'reset-password':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>;
      case 'two-factor':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>;
      default:
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>;
    }
  };

  const handleChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : value
    }));

    if (name === 'email') {
      onEmailChange(value);
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    if (customValidation) {
      return customValidation(formData, setErrors);
    }

    const newErrors = {};

    if (type === 'login' || type === 'register' || type === 'forgot-password') {
      const emailValidationMessage = getEmailValidationError(formData.email, {
        requiredMessage:
          type === 'login' ? 'Please enter a User ID' : 'Please enter an email address',
      });
      validateEmailForSubmit(formData.email);
      if (emailValidationMessage) {
        newErrors.email = emailValidationMessage;
      }
    }

    // Login validation
    if (type === 'login') {

      if (!formData.password) {
        newErrors.password = 'Please provide a password';
      } else if (formData.password.length < 5) {
        newErrors.password = 'Your password must be at least 5 characters long';
      }
    }

    // Register validation
    if (type === 'register') {
      if (!formData.first_name || formData.first_name.trim().length < 3) {
        newErrors.first_name = 'First name must be at least 3 characters';
      } else if (!/^[a-zA-Z_ ]*$/.test(formData.first_name)) {
        newErrors.first_name = 'Please enter only letters';
      }

      if (!formData.last_name || formData.last_name.trim().length < 3) {
        newErrors.last_name = 'Last name must be at least 3 characters';
      } else if (!/^[a-zA-Z_ ]*$/.test(formData.last_name)) {
        newErrors.last_name = 'Please enter only letters';
      }

      if (!formData.password) {
        newErrors.password = 'Please enter a password';
      } else if (formData.password.length < 11) {
        newErrors.password = 'Password must be at least 11 characters long';
      } else if (!/(?=.*[a-z])/.test(formData.password)) {
        newErrors.password = 'Password must include lowercase letters';
      } else if (!/(?=.*[A-Z])/.test(formData.password)) {
        newErrors.password = 'Password must include uppercase letters';
      } else if (!/(?=.*\d)/.test(formData.password)) {
        newErrors.password = 'Password must include numbers';
      } else if (!/\W/.test(formData.password)) {
        newErrors.password = 'Password must include symbols';
      }

      if (!formData.passwordConfirmation) {
        newErrors.passwordConfirmation = 'Please provide a confirm password';
      } else if (formData.password !== formData.passwordConfirmation) {
        newErrors.passwordConfirmation = 'Confirm password should be same as password';
      }
    }

    // Reset password validation
    if (type === 'reset-password') {
      if (!formData.password) {
        newErrors.password = 'Please provide a password';
      } else if (formData.password.length < 11) {
        newErrors.password = 'Password must be at least 11 characters long';
      } else if (!/(?=.*[a-z])/.test(formData.password)) {
        newErrors.password = 'Password must include lowercase letters';
      } else if (!/(?=.*[A-Z])/.test(formData.password)) {
        newErrors.password = 'Password must include uppercase letters';
      } else if (!/(?=.*\d)/.test(formData.password)) {
        newErrors.password = 'Password must include numbers';
      } else if (!/\W/.test(formData.password)) {
        newErrors.password = 'Password must include symbols';
      }

      if (!formData.passwordConfirmation) {
        newErrors.passwordConfirmation = 'Please provide a confirm password';
      } else if (formData.password !== formData.passwordConfirmation) {
        newErrors.passwordConfirmation = 'New password does not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        // Default behavior
        await new Promise(resolve => setTimeout(resolve, 2000));
        alert(`${type} successful!`);
        
        // Default navigation
        const basePath = appType === 'admin' ? '/admin' : appType === 'airliner' ? '/airliner' : '/';
        navigate(redirectPaths.success || basePath);
      }
    } catch (error) {
      // Check if account is self-deactivated (shows reactivation button)
      // vs admin-disabled (shows support message only)
      if (error?.account_status === 'self_deactivated') {
        setShowInactiveModal(true);
      } else if (error?.account_status === 'admin_disabled') {
        setErrors({ submit: error.message || 'Your account has been disabled. Please contact support.' });
      } else {
        setErrors({ submit: error.message || `${type} failed. Please try again.` });
      }
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  const getBasePath = () => {
    switch (appType) {
      case 'admin': return '/admin/auth';
      case 'airliner': return '/airliner/auth';
      default: return '';
    }
  };

  const basePath = getBasePath();

  return (
    <AuthLayout
      title={title}
      subtitle={subtitle}
      icon={getIcon()}
      iconBgColor={theme.iconBgColor}
      iconColor={theme.iconColor}
    >
      <AccountInactiveModal
        isOpen={showInactiveModal}
        onClose={() => setShowInactiveModal(false)}
        email={formData.email}
      />
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Common Fields */}
        {(type === 'login' || type === 'register' || type === 'forgot-password') && (
          <div>
            <label className="block text-sm font-medium text-jet-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email ? formData.email.toUpperCase() : ''}
              onChange={handleChange}
              onBlur={(e) => onEmailBlur(e.target.value)}
              placeholder="Enter your email"
              style={{ textTransform: 'uppercase' }}
              className={getEmailValidationInputClass(
                'w-full px-3 py-2 border rounded-md focus:outline-none transition-colors',
                {
                  touched: emailTouched,
                  error: emailError,
                  value: formData.email,
                  defaultBorderClass: 'border-jet-gray-300 focus:border-jet-yellow',
                }
              )}
            />
            <EmailFieldFeedback
              touched={emailTouched}
              error={emailError || errors.email}
              value={formData.email}
            />
          </div>
        )}

        {(type === 'login' || type === 'register' || type === 'reset-password') && (
          <div className="relative">
            <FormField
              label={type === 'reset-password' ? 'New Password' : 'Password'}
              name="password"
              value={formData.password || ''}
              onChange={handleChange}
              placeholder={type === 'reset-password' ? 'Enter your new password' : 'Enter your password'}
              error={errors.password}
              showPasswordToggle
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
            />
            
            {showPasswordGenerator && (
              <button
                type="button"
                onClick={generatePassword}
                className={`absolute right-0 top-8 ${theme.buttonColor} px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                title="Generate Password"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Remember Me Checkbox - Only for login */}
        {type === 'login' && (
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe || false}
                onChange={handleChange}
                className={`w-4 h-4 border-jet-gray-300 rounded focus:ring-2 cursor-pointer ${
                  appType === 'admin' 
                    ? 'text-jet-red-600 focus:ring-jet-red-500' 
                    : 'text-jet-yellow focus:ring-jet-yellow'
                }`}
              />
              <span className="ml-2 text-sm text-jet-gray-600">Remember me</span>
            </label>
          </div>
        )}

        {(type === 'register' || type === 'reset-password') && (
          <FormField
            label="Confirm Password"
            name="passwordConfirmation"
            value={formData.passwordConfirmation || ''}
            onChange={handleChange}
            placeholder="Confirm your password"
            error={errors.passwordConfirmation}
            showPasswordToggle
            showPassword={showConfirmPassword}
            onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
          />
        )}

        {/* Additional Fields */}
        {additionalFields.map((field, index) => (
          <FormField
            key={index}
            {...field}
            value={formData[field.name] || ''}
            onChange={handleChange}
            error={errors[field.name]}
            style={field.type !== 'password' ? { textTransform: 'uppercase' } : {}}
          />
        ))}

        {/* Submit Error */}
        {errors.submit && (
          <div className={`border rounded-md p-3 ${
            appType === 'admin' 
              ? 'bg-jet-red-50 border-jet-red-200' 
              : 'bg-jet-red-50 border-jet-red-200'
          }`}>
            <p className="text-sm text-jet-red-600">{errors.submit}</p>
          </div>
        )}

        {/* Submit Button */}
        <LoadingButton
          type="submit"
          loading={loading}
          loadingText={`${type === 'login' ? 'Signing in' : type === 'register' ? 'Creating account' : 'Processing'}...`}
          className={theme.buttonColor}
        >
          {type === 'login' ? 'Sign In' : 
           type === 'register' ? 'Create Account' :
           type === 'forgot-password' ? 'Send Reset Instructions' :
           type === 'reset-password' ? 'Reset Password' : 'Submit'}
        </LoadingButton>

        {/* Navigation Links */}
        <div className="text-center space-y-3">
          {type === 'login' && (
            <>
              <button
                type="button"
                onClick={() => navigate(`${basePath}/forgot-password`)}
                className={`${theme.linkColor} text-sm transition-colors`}
              >
                Forgot your password?
              </button>
              
              <div className="text-sm text-jet-gray-600">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate(`${basePath}/register`)}
                  className={`${theme.linkColor} font-medium`}
                >
                  Sign Up
                </button>
              </div>
            </>
          )}

          {type === 'register' && (
            <div className="text-sm text-jet-gray-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate(`${basePath}/login`)}
                className={`${theme.linkColor} font-medium`}
              >
                Sign In
              </button>
            </div>
          )}

          {(type === 'forgot-password' || type === 'reset-password') && (
            <button
              type="button"
              onClick={() => navigate(`${basePath}/login`)}
              className={`${theme.linkColor} text-sm transition-colors`}
            >
              ← Back to Login
            </button>
          )}
        </div>

        {/* Back to Main Site */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-jet-gray-600 hover:text-jet-gray-900 text-sm transition-colors"
          >
            ← Back to Main Site
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default UniversalAuthPage;